import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import {
  GridComponent,
  SelectionSettingsModel,
  SelectionType,
  PagerComponent,
  PageEventArgs,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import { useAppSelector } from '@store/hooks'

import StatusBadge from '@components/common/StatusBadge/StatusBadge.component'
import { StatusTypes } from '@dataTypes/claimFeature.types'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@store/index'
import {
  deleteFileAction,
  toggleFileFavoriteAction,
  downloadFileAction,
  uploadFileAction,
  fetchFolderById,
} from '@store/actions/folder.action'
import { Dropdown } from 'react-bootstrap'
import './ImageViewerListTableStyle.css'
import { IFolder, IFile } from '@dataTypes/folder.types'
import { ISelectedFile } from '../ImageViewerList/hooks/useImageViewerState'
import ConfirmBlockModal from '@components/layout/Modals/ConfirmBlockModal/ConfirmBlockModal.component'

import dicom_icon from '@assets/images/dicom_icon.svg'
import { Button } from '@components/common'
import { NoteContextType } from '@dataTypes/note.type'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'

import { logger } from '@utils/safeLogger'

interface ImageViewerListTableProps {
  selectedFiles: ISelectedFile[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<ISelectedFile[]>>
  searchTerm: string
  favoritesOnly: boolean
  files: IFile[]
  loading: boolean
  selectedFolder: IFolder | null
}

const ImageViewerListTable: React.FC<ImageViewerListTableProps> = ({
  selectedFiles,
  setSelectedFiles,
  searchTerm,
  favoritesOnly,
  files,
  loading,
  selectedFolder,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { selectedPatient } = useAppSelector((state) => state.patient)
  const activeClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const gridRef = useRef<GridComponent | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Page size and pagination state
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSizeOptions = [10, 25, 50, 100]

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [fileToDelete, setFileToDelete] = useState<string>('')
  const { openNoteEditor } = useNoteEditorOverlay()

  // Use files directly since they're already filtered by useImageViewerState
  // (includes searchTerm, favoritesOnly, and searchFilters)
  const filteredFiles = useMemo(() => {
    if (!files || !Array.isArray(files)) {
      return []
    }
    return files
  }, [files])

  // Calculate total count for pagination
  const total = filteredFiles.length

  // Get paginated files
  const paginatedFiles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredFiles.slice(startIndex, endIndex)
  }, [filteredFiles, currentPage, pageSize])

  // Track if we're syncing to prevent infinite loops
  const isSyncing = useRef(false)
  const headerCheckboxHandlerAttached = useRef(false)

  // Sync grid selection with selectedFiles state (for header "Select All" functionality)
  // This effect ensures selection is synced when switching from grid to table view
  useEffect(() => {
    const performSync = (): void => {
      if (!gridRef.current || !paginatedFiles.length || isSyncing.current) {
        return
      }

      const selectedFileIds = new Set(selectedFiles.map((f) => f.file_id))

      // Get all rows (data objects) that should be selected
      const rowsToSelect = paginatedFiles.filter((file) =>
        selectedFileIds.has(file.file_id),
      )

      // Get current selected rows from grid
      const currentSelectedRows = gridRef.current.getSelectedRows()
      const currentSelectedFileIds = new Set(
        currentSelectedRows.map((row: any) => row.file_id),
      )

      // Check if selection needs to be updated
      const needsUpdate =
        rowsToSelect.length !== currentSelectedRows.length ||
        paginatedFiles.some(
          (file) =>
            (selectedFileIds.has(file.file_id) &&
              !currentSelectedFileIds.has(file.file_id)) ||
            (!selectedFileIds.has(file.file_id) &&
              currentSelectedFileIds.has(file.file_id)),
        )

      if (!needsUpdate) {
        return
      }

      // Set syncing flag FIRST to prevent callbacks from modifying selectedFiles
      isSyncing.current = true

      // Use Syncfusion selection API to ensure internal state matches selectedFiles
      const indexesToSelect: number[] = []
      rowsToSelect.forEach((file) => {
        const index = paginatedFiles.findIndex(
          (f) => f.file_id === file.file_id,
        )
        if (index !== -1) {
          indexesToSelect.push(index)
        }
      })

      const selection = gridRef.current?.selectionModule
      const gridHasRows =
        !!gridRef.current &&
        typeof gridRef.current.getRows === 'function' &&
        Array.isArray(gridRef.current.getRows())

      if (selection && gridHasRows) {
        try {
          if (indexesToSelect.length > 0) {
            selection.selectRows(indexesToSelect)
          } else {
            selection.clearSelection()
          }
        } catch (err) {
          // Grid not ready; skip selection sync
        }
      } else {
        // Grid not ready; avoid calling selectRows to prevent errors
      }

      if (rowsToSelect.length > 0) {
        // Use setTimeout to ensure grid is ready
        setTimeout(() => {
          if (!gridRef.current || rowsToSelect.length === 0) {
            isSyncing.current = false
            return
          }

          try {
            const gridElement = gridRef.current.element
            if (gridElement) {
              const rowElements = gridElement.querySelectorAll('.e-row')

              // Check if all items in filteredFiles are selected (for header checkbox)
              const allFilteredItemsSelected =
                filteredFiles.length > 0 &&
                filteredFiles.every((file) => selectedFileIds.has(file.file_id))
              const someItemsSelected = filteredFiles.some((file) =>
                selectedFileIds.has(file.file_id),
              )

              // Sync header checkbox
              const headerRow = gridElement.querySelector('.e-headercell')
              if (headerRow) {
                const headerCheckbox = headerRow.querySelector(
                  'input.e-checkbox, input[type="checkbox"]',
                ) as HTMLInputElement

                if (headerCheckbox) {
                  const shouldHeaderBeChecked = allFilteredItemsSelected
                  const shouldHeaderBeIndeterminate =
                    someItemsSelected && !allFilteredItemsSelected

                  if (headerCheckbox.checked !== shouldHeaderBeChecked) {
                    // Update checkbox state
                    headerCheckbox.checked = shouldHeaderBeChecked
                    headerCheckbox.indeterminate = shouldHeaderBeIndeterminate
                    headerCheckbox.setAttribute(
                      'aria-checked',
                      shouldHeaderBeChecked ? 'true' : 'false',
                    )

                    // Trigger change event so Syncfusion recognizes the update
                    const changeEvent = new Event('change', { bubbles: true })
                    headerCheckbox.dispatchEvent(changeEvent)

                    // Also trigger click event to ensure Syncfusion updates its internal state
                    // But only if we're not already in a click handler
                    if (!isSyncing.current) {
                      // Use a small delay to avoid interfering with any ongoing operations
                      setTimeout(() => {
                        const clickEvent = new MouseEvent('click', {
                          bubbles: true,
                          cancelable: true,
                        })
                        // Don't actually trigger click, just update visual state
                      }, 0)
                    }

                    // Update header checkbox wrapper classes
                    const headerCheckboxWrapper = headerCheckbox.closest(
                      '.e-checkbox-wrapper',
                    )
                    if (headerCheckboxWrapper) {
                      if (shouldHeaderBeChecked) {
                        headerCheckboxWrapper.classList.add('e-checked')
                        headerCheckboxWrapper.classList.remove('e-stop')
                      } else if (shouldHeaderBeIndeterminate) {
                        headerCheckboxWrapper.classList.add('e-stop')
                        headerCheckboxWrapper.classList.remove('e-checked')
                      } else {
                        headerCheckboxWrapper.classList.remove(
                          'e-checked',
                          'e-stop',
                        )
                      }
                    }
                  } else if (
                    headerCheckbox.indeterminate !== shouldHeaderBeIndeterminate
                  ) {
                    headerCheckbox.indeterminate = shouldHeaderBeIndeterminate
                    const headerCheckboxWrapper = headerCheckbox.closest(
                      '.e-checkbox-wrapper',
                    )
                    if (headerCheckboxWrapper) {
                      if (shouldHeaderBeIndeterminate) {
                        headerCheckboxWrapper.classList.add('e-stop')
                        headerCheckboxWrapper.classList.remove('e-checked')
                      } else {
                        headerCheckboxWrapper.classList.remove('e-stop')
                      }
                    }
                  }
                }
              }

              // Manually update ALL checkboxes to match selectedFiles state
              Array.from(rowElements).forEach((rowElement, index) => {
                try {
                  const checkbox = (rowElement as HTMLElement).querySelector(
                    'input.e-checkbox, input[type="checkbox"]',
                  ) as HTMLInputElement

                  if (checkbox) {
                    const file = paginatedFiles[index]
                    const shouldBeChecked =
                      file && selectedFileIds.has(file.file_id)

                    // Update checkbox state
                    if (checkbox.checked !== shouldBeChecked) {
                      checkbox.checked = shouldBeChecked
                      checkbox.setAttribute(
                        'aria-checked',
                        shouldBeChecked ? 'true' : 'false',
                      )

                      // Also update the parent label/wrapper classes for visual feedback
                      const checkboxWrapper = checkbox.closest(
                        '.e-checkbox-wrapper',
                      )
                      if (checkboxWrapper) {
                        if (shouldBeChecked) {
                          checkboxWrapper.classList.add('e-checked')
                        } else {
                          checkboxWrapper.classList.remove('e-checked')
                        }
                      }
                    }
                  }
                } catch (error) {
                  // Silent fail - continue with other checkboxes
                }
              })

              // Clear sync flag after a delay
              setTimeout(() => {
                isSyncing.current = false
              }, 300)
            } else {
              isSyncing.current = false
            }
          } catch (error) {
            logger.error('Error in selection sync:', error)
            isSyncing.current = false
          }
        }, 150)
      } else {
        isSyncing.current = false
      }
    }

    // Try to sync immediately
    performSync()

    // If grid isn't ready but we have selected files, retry after delays
    // This handles the case when switching from grid to table view
    if (!gridRef.current && selectedFiles.length > 0) {
      const retryTimeout1 = setTimeout(() => {
        performSync()
      }, 200)

      const retryTimeout2 = setTimeout(() => {
        performSync()
      }, 500)

      return () => {
        clearTimeout(retryTimeout1)
        clearTimeout(retryTimeout2)
      }
    }
  }, [selectedFiles, paginatedFiles, filteredFiles])

  // Handle header checkbox click to select/deselect all
  useEffect(() => {
    if (!gridRef.current) {
      return
    }

    const gridElement = gridRef.current.element
    if (!gridElement) {
      return
    }

    let headerCheckbox: HTMLInputElement | null = null

    const handleHeaderCheckboxClick = (e: Event): void => {
      // Skip if we're syncing
      if (isSyncing.current) {
        return
      }

      e.stopPropagation()
      e.preventDefault()

      // Use the current state at the time of click
      // Use filteredFiles (which now equals files) for consistency
      setSelectedFiles((prev) => {
        const selectedFileIds = new Set(prev.map((f) => f.file_id))
        const allFilteredItemsSelected =
          filteredFiles.length > 0 &&
          filteredFiles.every((file) => selectedFileIds.has(file.file_id))

        // Set syncing flag to prevent interference
        isSyncing.current = true

        if (allFilteredItemsSelected) {
          // Deselect all filtered files
          const filteredFileIds = new Set(filteredFiles.map((f) => f.file_id))
          setTimeout(() => {
            isSyncing.current = false
          }, 100)
          return prev.filter((file) => !filteredFileIds.has(file.file_id))
        } else {
          // Select all filtered files
          const existingIds = new Set(prev.map((f) => f.file_id))
          const newSelections = filteredFiles
            .filter((file) => !existingIds.has(file.file_id))
            .map((file) => ({
              file_id: file.file_id,
              study_uid: file.study_uid,
              series_uid: file.series_uid,
            }))
          setTimeout(() => {
            isSyncing.current = false
          }, 100)
          return [...prev, ...newSelections]
        }
      })
    }

    const setupHeaderCheckboxListener = (): void => {
      if (headerCheckboxHandlerAttached.current) {
        return
      }

      const headerRow = gridElement.querySelector('.e-headercell')
      if (headerRow) {
        const checkbox = headerRow.querySelector(
          'input.e-checkbox, input[type="checkbox"]',
        ) as HTMLInputElement

        if (checkbox && !headerCheckboxHandlerAttached.current) {
          headerCheckbox = checkbox
          headerCheckbox.addEventListener('click', handleHeaderCheckboxClick, {
            capture: true,
          })
          headerCheckboxHandlerAttached.current = true
        }
      }
    }

    // Setup listener after a short delay to ensure grid is rendered
    const timeoutId = setTimeout(setupHeaderCheckboxListener, 200)

    return () => {
      clearTimeout(timeoutId)
      if (headerCheckbox && headerCheckboxHandlerAttached.current) {
        headerCheckbox.removeEventListener('click', handleHeaderCheckboxClick)
        headerCheckboxHandlerAttached.current = false
      }
    }
  }, [filteredFiles, setSelectedFiles, paginatedFiles])

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  // Handle page change
  const onPageChange = (args: PageEventArgs): void => {
    const newPage = parseInt(args.currentPage as string, 10) || 1
    setCurrentPage(newPage)
  }

  // Handle favorite toggle
  const handleFavorite = async (fileId: string): Promise<void> => {
    try {
      const file = files.find((f) => f.file_id === fileId)
      if (file) {
        await dispatch(
          toggleFileFavoriteAction({
            fileId,
            isFavorite: !file.is_favorite,
          }),
        ).unwrap()
      }
    } catch (error) {
      logger.error('Failed to toggle favorite:', error)
    }
  }

  // Handle delete
  const handleDelete = async (fileId: string): Promise<void> => {
    try {
      await dispatch(deleteFileAction(fileId)).unwrap()
      // Remove from selected files if it was selected
      setSelectedFiles((prev) => prev.filter((file) => file.file_id !== fileId))
    } catch (error) {
      logger.error('Failed to delete file:', error)
    }
  }

  // Handle delete click - show confirmation modal
  const handleDeleteClick = (fileId: string): void => {
    setFileToDelete(fileId)
    setShowDeleteModal(true)
  }

  // Confirm delete after user confirms in modal
  const confirmDelete = (): void => {
    if (fileToDelete) {
      handleDelete(fileToDelete)
      setShowDeleteModal(false)
      setFileToDelete('')
    }
  }

  // Handle download
  const handleDownload = async (fileId: string): Promise<void> => {
    if (!fileId) return
    try {
      const file = files.find((f) => f.file_id === fileId)
      if (file) {
        await dispatch(downloadFileAction(file)).unwrap()
      }
    } catch (error) {
      logger.error('Failed to download file:', error)
    }
  }

  // Handle notes click - open Notes overlay in file context.
  const handleNotesClick = (fileId: string): void => {
    if (!fileId || !selectedPatient) return

    const file = files.find((f) => f.file_id === fileId)
    if (!file) return

    openNoteEditor({
      patient: selectedPatient,
      contextId: file.file_id,
      contextType: NoteContextType.FILES,
      contextName: file.file_name,
      pagePreviousState: { path: '/image-viewer' },
      openFirstNote: true,
      initialView: 'list',
      hideNotesTable: true,
      overlayBackLabel: 'Back to files',
      editorBackButtonLabel: 'Back to files',
      openContextNotesByDefault: true,
      notesFetchLoadingContainer: 'body',
      portalTarget: '.page-content',
    })
  }

  // Function to format path with spaces around forward slashes
  const formatPath = (path: string | undefined): string => {
    if (!path) return ''
    const segments = path.split('/')
    if (segments.length <= 2) return '/'

    return `/${segments.slice(2).join('/')}`
  }

  // Columns definition
  const columns = [
    {
      field: 'file_name',
      headerText: 'File name',
      width: '300',
      cssClass: 'left-align-header', // Keep left aligned
      template: (props: any) => (
        <div className="file-name-column">
          <button
            className="star"
            onClick={(e) => {
              e.stopPropagation()
              handleFavorite(props.file_id)
            }}
          >
            <i
              className={`ti ${props.is_favorite ? 'ti-star-filled' : 'ti-star'}`}
            />
          </button>
          <img
            src={dicom_icon}
            alt={props.file_type === 'pdf' ? 'PDF' : 'Image'}
            className="file-name-icon"
          />
          <span className="file-name">{props.file_name}</span>
        </div>
      ),
    },
    {
      field: 'file_location',
      headerText: 'File location',
      width: '150',
      cssClass: '', // Center align this column
      template: (props: any): React.ReactElement => (
        <div className="center-align-content">
          <span>{formatPath(props.file_path)}</span>
        </div>
      ),
    },
    {
      field: 'file_size',
      headerText: 'Size',
      width: '93',
      cssClass: 'center-align-header', // Center align this column
      template: (props: any): React.ReactElement => (
        <div className="center-align-content">
          <span>
            {props.file_size
              ? `${(props.file_size / (1024 * 1024)).toFixed(2)} MB`
              : ''}
          </span>
        </div>
      ),
    },
    {
      field: 'updated_at',
      headerText: 'Date updated',
      width: '157',
      cssClass: 'center-align-header', // Center align this column
      template: (props: any) => {
        const formatDate = (dateString: string | null | undefined): string => {
          if (!dateString) return ''
          try {
            const date = new Date(dateString)
            // Check if date is valid
            if (isNaN(date.getTime())) {
              return 'Invalid date'
            }
            return date.toLocaleString()
          } catch (error) {
            console.warn('Error formatting date:', dateString, error)
            return 'Invalid date'
          }
        }
        return (
          <div className="center-align-content">
            <span>{formatDate(props.date_time_updated_at)}</span>
          </div>
        )
      },
    },
    {
      field: 'notes',
      headerText: 'Notes',
      width: '70',
      cssClass: 'center-align-header', // Center align this column
      template: (props: any) => (
        <div className="center-align-content">
          <Button
            className="act-button act-button-notes"
            onClick={(e) => {
              e.stopPropagation()
              handleNotesClick(props.file_id)
            }}
          >
            <i className="mgc mgc_document_2_fill" />
          </Button>
        </div>
      ),
    },
    {
      field: 'file_type',
      headerText: 'Type',
      width: '70',
      cssClass: 'center-align-header', // Center align this column
      template: (props: any) => (
        <div className="center-align-content">
          <StatusBadge status={props.file_type as StatusTypes} />
        </div>
      ),
    },
    {
      field: 'actions',
      headerText: 'Actions',
      width: '70',
      allowSorting: false,
      cssClass: 'center-align-header',
      template: (props: any) => (
        <div className="text-center">
          <Button
            className={`act-button act-button-download `}
            onClick={(e) => {
              e.stopPropagation()
              handleDownload(props.file_id)
            }}
          >
            <i className="mgc mgc_download_2_line" />
          </Button>
          <Button
            className="act-button act-button-delete"
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteClick(props.file_id || '')
            }}
          >
            <i className="mgc mgc_delete_2_line" />
          </Button>
        </div>
      ),
    },
  ]

  // Selection settings for DataGridComponent
  const selectionSettings: SelectionSettingsModel = {
    type: 'Multiple' as SelectionType,
    checkboxOnly: false,
  }

  // Handle row selection - ONLY adds to selection, never removes
  const rowSelected = useCallback(
    (args: RowSelectEventArgs) => {
      // CRITICAL: Skip if we're syncing from header to prevent interference
      if (isSyncing.current) {
        return
      }

      const file = args.data as unknown as IFile
      if (file?.file_id) {
        // Only ADD to selection - deselection is handled by rowDeselected
        setSelectedFiles((prev) => {
          const isCurrentlySelected = prev.some(
            (selected) => selected.file_id === file.file_id,
          )
          if (isCurrentlySelected) {
            // Already selected, don't add again
            return prev
          } else {
            // Add to selection
            return [
              ...prev,
              {
                file_id: file.file_id,
                study_uid: file.study_uid,
                series_uid: file.series_uid,
              },
            ]
          }
        })
      }
    },
    [setSelectedFiles],
  )

  // Handle row deselection - ONLY removes from selection
  const rowDeselected = useCallback(
    (args: RowSelectEventArgs) => {
      // CRITICAL: Skip if we're syncing from header to prevent interference
      if (isSyncing.current) {
        return
      }

      const file = args.data as unknown as IFile
      if (file?.file_id) {
        // Remove from selection
        setSelectedFiles((prev) =>
          prev.filter((selected) => selected.file_id !== file.file_id),
        )
      }
    },
    [setSelectedFiles],
  )

  // Show loading state
  if (loading) {
    return (
      <div className="image-viewer-list-table">
        <div className="loading-container">
          <div>Loading files...</div>
        </div>
      </div>
    )
  }

  // Show no folder selected message
  if (!selectedFolder) {
    return (
      <div className="image-viewer-list-table">
        <div className="no-folder-selected">
          <div>Please select a folder to view files</div>
        </div>
      </div>
    )
  }

  // Handle file upload
  const handleFileUpload = async (
    uploadedFiles: FileList | File[],
  ): Promise<void> => {
    if (!selectedFolder || !selectedPatient || !activeClinicId) {
      return
    }

    const filesArray = Array.from(uploadedFiles)

    try {
      // Upload all files
      await Promise.all(
        filesArray.map((file) =>
          dispatch(
            uploadFileAction({
              file,
              patientId: selectedPatient.patient_id,
              clinicId: activeClinicId,
              folderId: selectedFolder.folder_id,
            }),
          ).unwrap(),
        ),
      )

      // Refresh folder to show new files
      dispatch(fetchFolderById(selectedFolder.folder_id))
    } catch (error) {
      logger.error('Failed to upload file:', error)
    }
  }

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files)
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  const handleEmptyStateClick = (): void => {
    fileInputRef.current?.click()
  }

  // Show empty state when folder has no files
  if (!files || !Array.isArray(files) || files.length === 0) {
    return (
      <div className="image-viewer-list-table">
        <div
          className={`empty-folder-state ${isDragging ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleEmptyStateClick}
          style={{ cursor: 'pointer' }}
        >
          <div className="empty-folder-icon">
            <i className="mgc_file_upload_fill"></i>
          </div>
          <div className="empty-folder-text">
            <div className="empty-folder-title">
              Drag and drop or{' '}
              <span style={{ color: '#185DD8', textDecoration: 'none' }}>
                choose file
              </span>
            </div>
            <div className="empty-folder-subtitle">No files to show here</div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            className="d-none"
            onChange={handleFileInputChange}
            multiple
          />
        </div>
      </div>
    )
  }

  return (
    <div className="image-viewer-list-table">
      <DataGridComponent
        ref={gridRef}
        data={paginatedFiles}
        columns={columns}
        pageSize={pageSize}
        showCheckbox={true}
        showCheckboxWidth={30}
        allowPaging={false} // Disable built-in paging
        selectionSettings={selectionSettings}
        rowSelected={rowSelected}
        rowDeselected={rowDeselected}
        rowHeight={36}
      />

      {/* Custom pagination with dropdown */}
      <div
        className="pagination-container"
        style={{
          marginTop: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Dropdown>
          <Dropdown.Toggle className="viewport-header-dropdown">
            {pageSize} results/page
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {pageSizeOptions.map((size) => (
              <Dropdown.Item
                key={size}
                onClick={() => handlePageSizeChange(size)}
                active={pageSize === size}
              >
                {size} results/page
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <PagerComponent
          totalRecordsCount={total}
          pageSize={pageSize}
          currentPage={currentPage}
          click={onPageChange}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmBlockModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setFileToDelete('')
        }}
        mainText="Delete File?"
        subText="The file will be deleted from this folder. This action cannot be undone."
        action="Delete"
        onConfirm={confirmDelete}
      />
    </div>
  )
}

export default ImageViewerListTable
